import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { areUserRolesIn } from '../../../../../helpers';
import { clientActions, providerAssignmentActions } from '../../../../../store';
import { ClinicalRelationships, Roles } from '../../../../../constants';
import { getClientOptions } from '../utils/general';

import type { RootState } from '../../../../../store';
import type { IOption } from '../../../../../utils/types';

interface UseClientOptionsParams {
  selectedClientId?: number;
}

function useClientOptions({ selectedClientId }: UseClientOptionsParams) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { list: clients = [] } = useSelector((state: RootState) => state.clients);
  const { list: providerAssignments = [] } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { provider } = useSelector((state: RootState) => state.providers);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
  }, []);

  useEffect(() => {
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [dispatch, getAccessTokenSilently, provider?.id, user?.roles]);

  const clientOptions = useMemo<IOption[]>(
    () => getClientOptions({ providerAssignments, clients, userRoles: user?.roles }),
    [clients, providerAssignments, user?.roles],
  );

  const selectedClientDetails = useMemo(() => {
    if (!selectedClientId) return null;
    return clients.find((client) => client.id === selectedClientId) ?? null;
  }, [clients, selectedClientId]);

  return {
    clients,
    clientOptions,
    selectedClientDetails,
    user,
  };
}

export default useClientOptions;
